/* ============================================================
   ManMade — Shop / Cart / Article / Search JS
   Single file, no dependencies. Event delegation throughout.
   ============================================================ */

'use strict';

function $(sel, ctx) { return (ctx || document).querySelector(sel); }
function $$(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }

function fmtMoney(cents) { return '$' + (cents / 100).toFixed(2); }

/* ----------------------------------------------------------
   1. CART — qty +/- + input + remove + discount

   Addresses lines by their server-issued line key (item.key) — *not* by
   1-based forloop index. Indexes shift whenever a line is removed, so
   using `id: key` keeps subsequent updates pinned to the correct line
   even after the cart is reordered server-side.

   Rapid clicks (rage +/+/+) coalesce into a single POST via a 180ms
   debounce per line. Optimistic UI updates the qty input, line total,
   and cart subtotal/total *before* the server round-trip so the cart
   feels instant. The server response then reconciles authoritative
   prices (discounts, inventory caps, etc.).
   ---------------------------------------------------------- */
var __mmLinePending = Object.create(null); // key -> { target, timer, inflight, unitNow }

async function cartChange(id, quantity) {
  var res = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ id: id, quantity: quantity })
  });
  if (!res.ok) throw new Error('cart-change failed');
  return res.json();
}

function refreshCartTotals(cart) {
  $$('[data-mm-cart-subtotal]').forEach(function(el) { el.textContent = fmtMoney(cart.items_subtotal_price); });
  $$('[data-mm-cart-total]').forEach(function(el)   { el.textContent = fmtMoney(cart.total_price); });
  $$('[data-mm-cart-count]').forEach(function(el) {
    el.textContent = cart.item_count; el.hidden = cart.item_count === 0;
  });
  $$('[data-mm-cart-item-count]').forEach(function(el) { el.textContent = cart.item_count; });
  $$('[data-mm-cart-item-word]').forEach(function(el) {
    el.textContent = cart.item_count === 1 ? 'item' : 'items';
  });
}

function rowForKey(key) {
  if (!key) return null;
  var escaped = (window.CSS && CSS.escape) ? CSS.escape(key) : key.replace(/(["\\])/g, '\\$1');
  return document.querySelector('[data-mm-line][data-key="' + escaped + '"]');
}

// Per-unit price for a row, in cents. Prefers data attributes (set
// server-side from item.final_price), falls back to the cached unit
// from a previous server response.
function lineUnitNow(row) {
  if (!row) return null;
  var key = row.dataset.key;
  var p = key ? __mmLinePending[key] : null;
  if (p && p.unitNow != null) return p.unitNow;
  var fromAttr = parseInt(row.dataset.unitNow, 10);
  return isNaN(fromAttr) ? null : fromAttr;
}
function lineUnitWas(row) {
  if (!row) return 0;
  var fromAttr = parseInt(row.dataset.unitWas, 10);
  return isNaN(fromAttr) ? 0 : fromAttr;
}

// Optimistic cart-wide subtotal/total/item-count. Sums every visible
// line's (pending target × unit price). Lines with target 0 are
// excluded because they're animating out.
function patchOptimisticTotals() {
  var sum = 0;
  var itemCount = 0;
  $$('[data-mm-line][data-key]').forEach(function(row) {
    var key = row.dataset.key;
    var p = __mmLinePending[key];
    var input = row.querySelector('.mm-line__qty-input');
    var qty = p ? p.target : (input ? (parseInt(input.value, 10) || 0) : 0);
    if (qty <= 0) return;
    itemCount += qty;
    var unit = lineUnitNow(row);
    if (unit != null) sum += unit * qty;
  });
  $$('[data-mm-cart-subtotal]').forEach(function(el) { el.textContent = fmtMoney(sum); });
  $$('[data-mm-cart-total]').forEach(function(el) { el.textContent = fmtMoney(sum); });
  $$('[data-mm-cart-count]').forEach(function(el) {
    el.textContent = itemCount; el.hidden = itemCount === 0;
  });
  $$('[data-mm-cart-item-count]').forEach(function(el) { el.textContent = itemCount; });
  $$('[data-mm-cart-item-word]').forEach(function(el) {
    el.textContent = itemCount === 1 ? 'item' : 'items';
  });
}

// Reconcile a single row from authoritative server item data.
function reconcileLine(row, item) {
  if (!row) return;
  var input = row.querySelector('.mm-line__qty-input');
  if (input && item && document.activeElement !== input) input.value = item.quantity;
  var nowEl = row.querySelector('[data-mm-line-total]');
  if (nowEl && item) nowEl.textContent = fmtMoney(item.final_line_price);
  // Update cached unit so subsequent optimistic math stays accurate.
  if (item && item.quantity > 0) {
    row.dataset.unitNow = Math.round(item.final_line_price / item.quantity);
  }
}

async function flushLine(key) {
  var p = __mmLinePending[key];
  if (!p || p.inflight) return;
  p.inflight = true;
  var sent = p.target;
  try {
    var cart = await cartChange(key, sent);
    var row = rowForKey(key);
    var item = cart.items.find(function(it) { return it.key === key; });
    if (item) {
      reconcileLine(row, item);
    } else if (row && sent === 0) {
      // Line removed. If cart is now empty, reload to re-render the
      // empty-state markup that lives in cart-page.liquid.
      if (cart.item_count === 0) {
        delete __mmLinePending[key];
        window.location.reload();
        return;
      }
      row.style.transition = 'opacity .22s ease, transform .22s ease';
      row.style.opacity = '0';
      row.style.transform = 'translateX(-12px)';
      setTimeout(function() { row.remove(); patchOptimisticTotals(); }, 240);
    }
    refreshCartTotals(cart);
    document.dispatchEvent(new CustomEvent('mm:cart:updated', { detail: cart }));

    var still = __mmLinePending[key];
    if (still) {
      // Re-flush only if the *user's* target moved during the in-flight
      // window. Comparing to server's authoritative qty would loop
      // forever if the server capped at inventory.
      if (still.target !== sent) {
        still.inflight = false;
        flushLine(key);
        return;
      }
      delete __mmLinePending[key];
    }
  } catch (e) {
    if (__mmLinePending[key]) __mmLinePending[key].inflight = false;
    // Server failed — re-sync from /cart.js so the UI doesn't drift.
    try {
      var r = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (r.ok) {
        var c = await r.json();
        refreshCartTotals(c);
        var row2 = rowForKey(key);
        var sItem = c.items.find(function(it) { return it.key === key; });
        if (row2 && sItem) reconcileLine(row2, sItem);
        patchOptimisticTotals();
      }
    } catch (_) { /* offline — leave UI alone */ }
    delete __mmLinePending[key];
    announce('Could not update cart. Please try again.');
  }
}

function queueLine(row, nextQty, immediate) {
  if (!row) return;
  var key = row.dataset.key;
  if (!key) return;
  var p = __mmLinePending[key];
  if (!p) {
    p = __mmLinePending[key] = {
      target: nextQty,
      timer: 0,
      inflight: false,
      unitNow: lineUnitNow(row)
    };
  } else {
    p.target = nextQty;
  }
  // Optimistic per-line UI
  var input = row.querySelector('.mm-line__qty-input');
  if (input && nextQty > 0 && document.activeElement !== input) input.value = nextQty;
  if (nextQty > 0) {
    var nowEl = row.querySelector('[data-mm-line-total]');
    var wasEl = row.querySelector('.mm-line__price-was');
    var unit = lineUnitNow(row);
    var unitWas = lineUnitWas(row);
    if (nowEl && unit != null) nowEl.textContent = fmtMoney(unit * nextQty);
    if (wasEl && unitWas) wasEl.textContent = fmtMoney(unitWas * nextQty);
    row.style.opacity = '';
    row.style.pointerEvents = '';
  } else {
    row.style.transition = 'opacity .15s ease';
    row.style.opacity = '0.5';
    row.style.pointerEvents = 'none';
  }
  patchOptimisticTotals();

  clearTimeout(p.timer);
  if (immediate) {
    flushLine(key);
  } else {
    p.timer = setTimeout(function() { flushLine(key); }, 180);
  }
}

function handleQty(action) {
  var row = action.closest('[data-mm-line]');
  if (!row) return;
  var input = row.querySelector('[data-mm-action="qty-input"]');
  if (!input) return;
  var key = row.dataset.key;
  var p = key ? __mmLinePending[key] : null;
  var type = action.dataset.mmAction;
  var next;
  if (type === 'qty-input') {
    // User typed a value directly — treat it as the authoritative target.
    // The debounce + idempotent server semantics make a same-value commit cheap.
    next = Math.max(1, parseInt(action.value, 10) || 1);
  } else {
    var current = p ? p.target : (parseInt(input.value, 10) || 1);
    next = current;
    if (type === 'qty-inc') next = current + 1;
    else if (type === 'qty-dec') next = Math.max(1, current - 1);
    if (next === current) return;
  }
  queueLine(row, next, false);
}

function handleRemove(action) {
  var row = action.closest('[data-mm-line]');
  if (!row) return;
  queueLine(row, 0, true);
}

async function applyDiscount(action) {
  var input = $('[data-mm-promo-input]');
  var msg   = $('[data-mm-promo-msg]');
  if (!input || !input.value.trim()) return;
  var code = input.value.trim();
  if (msg) { msg.hidden = true; msg.removeAttribute('data-state'); }
  try {
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: { 'discount_code': code } })
    });
    var successMsg = 'Code saved — applied at checkout.';
    if (msg) {
      msg.textContent = successMsg;
      msg.hidden = false;
    }
    announce(successMsg);
  } catch (e) {
    var errorMsg = 'Could not apply code. Try again.';
    if (msg) {
      msg.textContent = errorMsg;
      msg.setAttribute('data-state', 'error');
      msg.hidden = false;
    }
    announce(errorMsg);
  }
}

/* ----------------------------------------------------------
   2. COLLECTION TOOLBAR — sort
   ---------------------------------------------------------- */
function initSort() {
  $$('select[data-mm-action="sort"]').forEach(function(sel) {
    sel.addEventListener('change', function() {
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', sel.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  });
}

/* ----------------------------------------------------------
   3. ARTICLE SHARE — copy URL
   ---------------------------------------------------------- */
function copyUrl(btn) {
  var url = window.location.href;
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(url).then(function() { flash(btn, 'Copied!'); });
  } else {
    var t = document.createElement('textarea');
    t.value = url; document.body.appendChild(t); t.select();
    try { document.execCommand('copy'); } catch (e) {}
    document.body.removeChild(t);
    flash(btn, 'Copied!');
  }
}

function flash(btn, text) {
  var orig = btn.textContent;
  btn.textContent = text;
  // Mirror the message into a live region so screen-reader users hear the result.
  announce(text);
  setTimeout(function() { btn.textContent = orig; }, 1800);
}

// Lazily create a single visually-hidden live region for transient status
// messages (copy confirmations, etc.) — WCAG 4.1.3 Status Messages.
var _liveRegion = null;
function announce(text) {
  if (!_liveRegion) {
    _liveRegion = document.createElement('div');
    _liveRegion.setAttribute('role', 'status');
    _liveRegion.setAttribute('aria-live', 'polite');
    _liveRegion.setAttribute('aria-atomic', 'true');
    _liveRegion.className = 'visually-hidden';
    _liveRegion.id = 'mm-a11y-live';
    document.body.appendChild(_liveRegion);
  }
  // Clear then set on next frame so AT re-announces identical text.
  _liveRegion.textContent = '';
  requestAnimationFrame(function() { _liveRegion.textContent = text; });
}

/* ----------------------------------------------------------
   4. ARTICLE TOC — populate from .mm-prose h2
   ---------------------------------------------------------- */
function initToc() {
  var toc = $('[data-mm-toc]');
  if (!toc) return;
  var list = toc.querySelector('[data-mm-toc-list]');
  var content = $('[data-mm-article-content]');
  if (!list || !content) return;
  var heads = $$('.mm-prose h2', content);
  if (!heads.length) { toc.style.display = 'none'; return; }
  heads.forEach(function(h, i) {
    var id = h.id || ('section-' + i);
    h.id = id;
    var li = document.createElement('li');
    var a = document.createElement('a');
    a.href = '#' + id;
    a.textContent = h.textContent;
    li.appendChild(a);
    list.appendChild(li);
  });
}

/* ----------------------------------------------------------
   5. SEARCH — autofocus on empty queries
   Only fires when the user actually navigated *to* the search page intending
   to search; we skip when the user followed a deep anchor link or has set
   `prefers-reduced-motion` (which we treat as a strong hint to avoid
   focus-stealing on page load).
   ---------------------------------------------------------- */
function initSearch() {
  if (window.location.hash) return;
  var input = $('input[data-mm-search-autofocus]');
  if (!input) return;
  // Only autofocus if the user reached this page from outside the site or
  // came back to it without a prior search. Empty terms imply intent to type.
  if (input.value === '' && !document.activeElement.matches('input, textarea, select')) {
    input.focus({ preventScroll: true });
  }
}

/* ----------------------------------------------------------
   6. GIFT CARD — copy code
   ---------------------------------------------------------- */
function copyGiftCode(btn) {
  var code = btn.dataset.code;
  if (!code) return;
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(code).then(function() { flash(btn, '✓ Copied'); });
  } else {
    var t = document.createElement('textarea');
    t.value = code; document.body.appendChild(t); t.select();
    try { document.execCommand('copy'); } catch (e) {}
    document.body.removeChild(t);
    flash(btn, '✓ Copied');
  }
}

/* ----------------------------------------------------------
   7. PRINT
   ---------------------------------------------------------- */
function handlePrint() { window.print(); }

/* ----------------------------------------------------------
   CENTRAL CLICK DELEGATION
   ---------------------------------------------------------- */
document.addEventListener('click', function(e) {
  var action = e.target.closest('[data-mm-action]');
  if (!action) return;
  var type = action.dataset.mmAction;
  switch (type) {
    case 'qty-inc':
    case 'qty-dec':
      e.preventDefault(); handleQty(action); break;
    case 'remove-line':
      e.preventDefault(); handleRemove(action); break;
    case 'apply-discount':
      e.preventDefault(); applyDiscount(action); break;
    case 'copy-url':
      e.preventDefault(); copyUrl(action); break;
    case 'copy-gc':
      e.preventDefault(); copyGiftCode(action); break;
    case 'print':
      e.preventDefault(); handlePrint(); break;
    case 'toggle-protection':
      // Handled in change listener; the label re-fires the click on the checkbox.
      break;
  }
});

async function handleProtection(cb) {
  var variantId = parseInt(cb.dataset.variantId, 10);
  if (!variantId) return;
  var label = cb.closest('[data-mm-protect]');
  if (label) label.setAttribute('aria-busy', 'true');
  cb.disabled = true;
  try {
    var cartRes = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    var cart = await cartRes.json();
    var existingKey = null;
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].variant_id === variantId) { existingKey = cart.items[i].key; break; }
    }
    if (cb.checked && !existingKey) {
      var addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: { _protection: 'true' } }] })
      });
      if (!addRes.ok) throw new Error('add failed');
    } else if (!cb.checked && existingKey) {
      await cartChange(existingKey, 0);
    }
    window.location.reload();
  } catch (e) {
    cb.checked = !cb.checked;
  } finally {
    cb.disabled = false;
    if (label) label.removeAttribute('aria-busy');
  }
}

document.addEventListener('change', function(e) {
  var qty = e.target.closest('[data-mm-action="qty-input"]');
  if (qty) { handleQty(qty); return; }
  var protect = e.target.closest('[data-mm-action="toggle-protection"]');
  if (protect) handleProtection(protect);
});

document.addEventListener('DOMContentLoaded', function() {
  initSort();
  initToc();
  initSearch();
});
